import { DeviceUI } from "../components/DeviceUI";
import { blazonToDevice, Device, divisonAndOrdinaryAllowed } from "./Device";
import { deepCopy } from "./utils";

const API_PATH = "https://api.worldspinner.com/worldservice/heraldry";

export interface ChargePermutations {
    field: number[],
    division: number[],
    ordinary: number[],
};

/** Calls the API to render the design; returns an encoded image.
 *     Throws error on failure */
export async function apiEmblazon(device: Device): Promise<string> {
    try {
        const response = await fetch(`${API_PATH}/emblazon/?base64=true&shield=` + device.shield + "&size=1000", {
            "headers": {
                "accept": "application/json, text/plain, */*",
                "accept-language": "en-US,en;q=0.9",
                "cache-control": "no-cache",
                "content-type": "application/json;charset=UTF-8",
                "pragma": "no-cache",
                "sec-ch-ua": "\"Not?A_Brand\";v=\"8\", \"Chromium\";v=\"108\", \"Google Chrome\";v=\"108\"",
                "sec-ch-ua-mobile": "?0",
                "sec-ch-ua-platform": "\"macOS\"",
            },
            "body": calcBody(device),
            "method": "POST",
        });
        if (response.status === 200) {
            DeviceUI.imageBlob = await response.blob();
            const blobText = "data:image/png;base64," + await DeviceUI.imageBlob.text();
            return blobText;
        }
        else {
            console.error("HTTP-Error: ", response)
            throw new Error("apiEmblazon got bad response status " + response.status);
        }
    } catch (e) {
        // console.log("Device ERROR: ", e);
        throw e;
    }
}

function calcBody(device: Device): string {
    let charges = "null";
    if (device.field.charges) {
        charges = `{"nbr": "${device.field.charges.nbr}", "color": "${device.field.charges.color}", "name": "${device.field.charges.name}"}`;
    }
    let field = `{"color":"${device.field.color}","fullCharge":false,"charges":${charges}}`;

    let division = `null`;
    if (device.division) {
        let charges = 'null';
        if (device.division.charges) {
            charges = `{"nbr": "${device.division.charges.nbr}", "color": "${device.division.charges.color}", "name": "${device.division.charges.name}"}`;
        }
        division = `{"color":"${device.division.color}","kind":"${device.division.kind}","charges":${charges}}`;
    }

    let ordinary = `null`;
    if (device.ordinary) {
        let charges = 'null';
        if (device.ordinary.charges) {
            charges = `{"nbr": "${device.ordinary.charges.nbr}", "color": "${device.ordinary.charges.color}", "name": "${device.ordinary.charges.name}"}`;
        }
        ordinary = `{"color":"${device.ordinary.color}","kind":"${device.ordinary.kind}","charges":${charges}}`;
    }

    const result = `{"field":${field}, "division":${division}, "ordinary":${ordinary}}`;
    console.log("rendering device: " + result);
    return result;
}

export function apiConformDevice(device: Device, perms: ChargePermutations) {
    if (device.field.charges && Number(device.field.charges.nbr) === 0) {
        device.field.charges = undefined;
    }
    let fieldCharges = device.field.charges?.nbr;
    const allowedFieldCharges = perms.field;
    if (fieldCharges) {
        if (!allowedFieldCharges.includes(fieldCharges)) {
            console.log(`apiConformDevice: fieldCharges was ${fieldCharges}, not in ${allowedFieldCharges}`);
            fieldCharges = allowedFieldCharges[allowedFieldCharges.length - 1];
            if (fieldCharges) {
                device.field.charges!.nbr = fieldCharges;
                console.log(`apiConformDevice: changed fieldCharges to ${device.field.charges!.nbr}`);
            } else {
                device.field.charges = undefined;
                console.log(`apiConformDevice: dropped fieldCharges`);
            }
        }
    }

    if (device.division?.charges && Number(device.division.charges.nbr) === 0) {
        device.division.charges = undefined;
    }
    let divisionCharges = device.division?.charges?.nbr;
    const allowedDivisionCharges = perms.division;
    if (divisionCharges) {
        if (!allowedDivisionCharges.includes(divisionCharges)) {
            console.log(`apiConformDevice: divisionCharges was ${divisionCharges}, not in ${allowedDivisionCharges}`);
            divisionCharges = allowedDivisionCharges[allowedDivisionCharges.length - 1];
            if (divisionCharges) {
                device.division!.charges!.nbr = divisionCharges;
                console.log(`apiConformDevice: changed divisionCharges to ${device.division!.charges!.nbr}`);
            } else {
                device.division!.charges = undefined;
                console.log(`apiConformDevice: dropped divisionCharges`);
            }
        }
    }

    if (device.ordinary?.charges && Number(device.ordinary.charges.nbr) === 0) {
        device.ordinary.charges = undefined;
    }
    let ordinaryCharges = device.ordinary?.charges?.nbr;
    const allowedOrdinaryCharges = perms.ordinary;
    if (ordinaryCharges) {
        if (!allowedOrdinaryCharges.includes(ordinaryCharges)) {
            console.log(`apiConformDevice: ordinaryCharges was ${ordinaryCharges}, not in ${allowedOrdinaryCharges}`);
            ordinaryCharges = allowedOrdinaryCharges[allowedOrdinaryCharges.length - 1];
            if (ordinaryCharges) {
                device.ordinary!.charges!.nbr = ordinaryCharges;
                console.log(`apiConformDevice: changed ordinaryCharges to ${device.ordinary!.charges!.nbr}`);
            } else {
                device.ordinary!.charges = undefined;
                console.log(`apiConformDevice: dropped ordinaryCharges`);
            }
        }
    }

    if (!divisonAndOrdinaryAllowed(device.division?.kind, device.ordinary?.kind)) {
        device.ordinary = undefined;
    }

}

/** Get an entirely random design
    Throws error on failure */
// TODO - store it, update the display
// TODO - respect shield shape from General
export async function apiGetRandomDesign(): Promise<Device> {
    try {
        // console.log("apiGetRandomDesign");

        const response = await fetch(`${API_PATH}/blazon/random/?serialized=true`, {
            "headers": {
                "accept": "application/json, text/plain, */*",
                "accept-language": "en-US,en;q=0.9",
                "cache-control": "no-cache",
                "content-type": "application/json;charset=UTF-8",
                "pragma": "no-cache",
                "sec-ch-ua": "\"Not?A_Brand\";v=\"8\", \"Chromium\";v=\"108\", \"Google Chrome\";v=\"108\"",
                "sec-ch-ua-mobile": "?0",
                "sec-ch-ua-platform": "\"macOS\"",
            },
            "method": "GET",
        });

        if (response.status === 200) {
            const randomBlazon = await response.json();
            console.log("Response = ", randomBlazon);
            return blazonToDevice(randomBlazon);
        }
        else {
            // console.error("HTTP-Error: ", response);
            throw new Error("apiGetRandomDesign got bad response status " + response.status);
        }
    } catch (e) {
        console.log("apiGetRandomDesign ERROR: ", e);
        throw e;
    }
}

/** What are valid changes in general?
    Throws error on failure */
export async function apiGetOptions(device: Device): Promise<any> {
    try {
        console.log("apiGetOptions");

        const response = await fetch(`${API_PATH}/options`, {
            "headers": {
                "accept": "application/json, text/plain, */*",
                "accept-language": "en-US,en;q=0.9",
                "cache-control": "no-cache",
                "content-type": "application/json;charset=UTF-8",
                "pragma": "no-cache",
                "sec-ch-ua": "\"Not?A_Brand\";v=\"8\", \"Chromium\";v=\"108\", \"Google Chrome\";v=\"108\"",
                "sec-ch-ua-mobile": "?0",
                "sec-ch-ua-platform": "\"macOS\"",
            },
            "body": calcBody(device),
            // "body": deviceToBlazon(device),
            "method": "POST",
        });

        if (response.status !== 200) {
            console.error("HTTP-Error: ", response)
            throw new Error("apiGetOptions got bad response status " + response.status);
        }
        return await response.json();
    } catch (e) {
        console.log("apiGetOptions ERROR: ", e);
        throw e;
    }
}

/** What are valid changes in general?
    Throws error on failure */
export async function apiGetChargePermutations(device: Device): Promise<ChargePermutations> {
    try {
        const deviceCopy = deepCopy(device);
        deviceCopy.shield = undefined;

        const response = await fetch(`${API_PATH}/chargepermutations/?shield=` + device.shield, {
            "headers": {
                "accept": "application/json, text/plain, */*",
                "accept-language": "en-US,en;q=0.9",
                "cache-control": "no-cache",
                "content-type": "application/json;charset=UTF-8",
                "pragma": "no-cache",
                "sec-ch-ua": "\"Not?A_Brand\";v=\"8\", \"Chromium\";v=\"108\", \"Google Chrome\";v=\"108\"",
                "sec-ch-ua-mobile": "?0",
                "sec-ch-ua-platform": "\"macOS\"",
            },
            "body": calcBody(device),
            // "body": deviceToBlazon(deviceCopy),
            "method": "POST",
        });
        if (response.status !== 200) {
            console.error("HTTP-Error: ", response)
            throw new Error("apiGetChargePermutations got bad response status " + response.status);
        }
        const result = await response.json();
        // console.log("apiGetChargePermutations: ", result);
        result.field = result.field.map((x: any) => Number(x));
        result.division = result.division.map((x: any) => Number(x));
        result.ordinary = result.ordinary.map((x: any) => Number(x));
        return result;
    } catch (e) {
        console.log("apiGetOptions ERROR: ", e);
        throw e;
    }
}

